import React, { type ReactElement, type ReactNode } from 'react';

import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { captureException } from '@sentry/react';
import { exception as GAException } from 'react-ga';

import TMXErrorView from 'components/errors/TMXErrorView';

const ExceptionContainer = ({ errorId }) => <TMXErrorView errorId={errorId} type="react" />;

class ErrorBoundary extends React.Component<{ appInsights: ReactPlugin; children?: ReactNode }> {
  state = { hasError: false, errorId: '' };

  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    const errorId = `RE-${Date.now().toString(36).toUpperCase()}`;
    this.setState({ errorId });
    if (this.props.appInsights?.isInitialized()) {
      this.props.appInsights?.trackException({
        error: error,
        exception: error,
        severityLevel: SeverityLevel.Error,
        properties: {
          ...errorInfo,
          errorId,
        },
      });
    } else {
      console.warn(`Application insights not loaded. ${errorId} relates to Error:`, error);
    }
    GAException({
      description: error,
      fatal: true,
    });
    captureException(error);
  }
  render() {
    if (this.state.hasError) {
      return <ExceptionContainer errorId={this.state.errorId} />;
    }
    return this.props.children as ReactElement;
  }
}

const ErrorBoundaryContainer = ({ children }) => {
  const appInsights = useAppInsightsContext();
  return <ErrorBoundary appInsights={appInsights}>{children}</ErrorBoundary>;
};

export default ErrorBoundaryContainer;
